import {
  BaseGradientLightEffectInput,
  BaseLightEffectInput,
  BaseLightingFeature,
} from '../../../../features/base-lighting/contract'
import {
  BASE_CHARGE_LIGHT_DISPLAY_MODE,
  type BaseChargeLightDisplayMode,
  type BaseCurrentLightEffect,
  type BaseGradientLightEffect,
  type BaseLightEffect,
} from '../../../../types/base'
import type {
  BaseLightingTarget,
  LightingCapability,
  LightingEffectDefinition,
} from '../../../../types/lighting'
import { LIGHTING_EDITABLE_FIELD, LIGHTING_TARGET } from '../../../../types/lighting'
import { DeviceError, DeviceErrorCode } from '../../../../errors/DeviceError'
import { sendBaseCommand, toBytes } from '../../protocols/base-command/command-io'
import type { BaseDockProfile } from '../../capabilities/base-command/profile'
import { BaseDevice } from '../../../../runtime/BaseDevice'

const CHARGE_LIGHT_DISPLAY_MODE_COMMAND_VALUE: Record<BaseChargeLightDisplayMode, number> = {
  [BASE_CHARGE_LIGHT_DISPLAY_MODE.Ambient]: 0x01,
  [BASE_CHARGE_LIGHT_DISPLAY_MODE.Charging]: 0x00,
}

const CHARGE_LIGHT_DISPLAY_MODE_BY_COMMAND_VALUE: Record<number, BaseChargeLightDisplayMode> = {
  0x01: BASE_CHARGE_LIGHT_DISPLAY_MODE.Ambient,
  0x00: BASE_CHARGE_LIGHT_DISPLAY_MODE.Charging,
}

/**
 * 读取底座响应字段。
 *
 * P6 会省略值为 0 的响应尾部字段；原厂客户端以清零的定长缓冲区读取，
 * 因此缺失字段需按 0 处理，不能直接访问越界的 DataView。
 */
function readResponseByte(data: DataView, index: number, fallback = 0): number {
  return index >= 0 && index < data.byteLength ? data.getUint8(index) : fallback
}

/**
 * 底座灯效实现。
 *
 * 两类底座的公开能力基本一致，但命令字不同，因此通过 driver 在内部选择命令表。
 */
export class RapooBaseLighting extends BaseLightingFeature {
  constructor(
    device: BaseDevice,
    private readonly profile: BaseDockProfile,
  ) {
    super(device)
  }

  /** 读取底座灯区的统一灯效元数据。 */
  async getEffects(
    target: BaseLightingTarget = LIGHTING_TARGET.BaseLightBand,
  ): Promise<LightingEffectDefinition[]> {
    const lightingConfig = this.device.manifest.capabilities.find(
      (cap: any) => cap.type === 'lighting',
    ) as LightingCapability | undefined
    if (!lightingConfig) return []

    return lightingConfig.config.effects.filter((effect) => effect.target === target)
  }

  /** 读取指定灯效的完整配置。 */
  async getLightEffect(
    mode: number,
    target: BaseLightingTarget = LIGHTING_TARGET.BaseLightBand,
  ): Promise<BaseLightEffect> {
    if (this.isGradientEffect(mode, target)) {
      const start = await this.getGradientLightEffect(mode, 0)
      const end = await this.getGradientLightEffect(mode, 15)
      return {
        mode,
        brightness: start.brightness,
        frequency: 0,
        color: start.color,
        gradientDirection: 3,
        rgbGroup: [
          { r: start.color[0], g: start.color[1], b: start.color[2] },
          { r: end.color[0], g: end.color[1], b: end.color[2] },
        ],
        raw: start.raw,
      }
    }

    // target 用来区分灯带和屏幕灯：ESPR7/ESPR7PRO 这类 logo 屏会走屏幕灯命令。
    const data = await sendBaseCommand(
      this.device,
      this.profile.config,
      this.getLightCommand(target),
      [mode],
    )
    return {
      mode,
      // P5/P6 的前几个动态灯效回包长度并不一致，1.0 也是按实际字段是否存在再取值。
      brightness: readResponseByte(data, 2),
      frequency: readResponseByte(data, 3),
      color: [readResponseByte(data, 4), readResponseByte(data, 5), readResponseByte(data, 6)],
      gradientDirection: 1,
      rgbGroup: [
        {
          r: readResponseByte(data, 4),
          g: readResponseByte(data, 5),
          b: readResponseByte(data, 6),
        },
        {
          r: readResponseByte(data, 4),
          g: readResponseByte(data, 5),
          b: readResponseByte(data, 6),
        },
      ],
      raw: toBytes(data),
    }
  }

  /** 写入指定灯效的亮度、速度和颜色。 */
  async setLightEffect(input: BaseLightEffectInput): Promise<void> {
    const target = input.target ?? LIGHTING_TARGET.BaseLightBand
    if (this.isGradientEffect(input.mode, target)) {
      const startColor = input.rgbGroup?.[0]
      const endColor = input.rgbGroup?.[1] ?? startColor
      await Promise.all([
        this.setGradientLightEffect({
          mode: input.mode,
          ledIndex: 0,
          color: [
            startColor?.r ?? input.color[0],
            startColor?.g ?? input.color[1],
            startColor?.b ?? input.color[2],
          ],
          brightness: input.brightness,
        }),
        this.setGradientLightEffect({
          mode: input.mode,
          ledIndex: 15,
          color: [
            endColor?.r ?? input.color[0],
            endColor?.g ?? input.color[1],
            endColor?.b ?? input.color[2],
          ],
          brightness: input.brightness,
        }),
      ])
      return
    }

    await sendBaseCommand(this.device, this.profile.config, this.setLightCommand(target), [
      input.mode,
      input.brightness,
      input.frequency,
      ...input.color.slice(0, 3),
    ])
  }

  /** 读取当前生效灯效、充电状态和总开关。 */
  async getCurrentEffect(
    target: BaseLightingTarget = LIGHTING_TARGET.BaseLightBand,
  ): Promise<BaseCurrentLightEffect> {
    const data = await sendBaseCommand(
      this.device,
      this.profile.config,
      this.getCurrentCommand(target),
    )
    const chargeState = readResponseByte(data, 2)
    // P5/P6（Telink_Base）没有充电状态灯，灯效都是氛围灯。
    // 只有带 show_effect 的 VT_nrf54L_Base 才用 byte2 区分充电灯和氛围灯。
    const supportsChargeLight = Boolean(this.commands.show_effect)
    return {
      modeId: readResponseByte(data, 1),
      chargeState,
      chargeLightDisplayMode: supportsChargeLight
        ? (CHARGE_LIGHT_DISPLAY_MODE_BY_COMMAND_VALUE[chargeState] ?? null)
        : null,
      isOn: readResponseByte(data, 3) === 1,
      raw: toBytes(data),
    }
  }

  /** 设置当前生效灯效。 */
  async setCurrentEffect(
    mode: number,
    target: BaseLightingTarget = LIGHTING_TARGET.BaseLightBand,
  ): Promise<void> {
    await sendBaseCommand(this.device, this.profile.config, this.setCurrentCommand(target), [mode])
  }

  /** 开关底座灯效总开关。 */
  async switchLight(isOn: boolean): Promise<void> {
    await sendBaseCommand(this.device, this.profile.config, this.commands.switch_light, [
      isOn ? 0x01 : 0x00,
    ])
  }

  /** 设置鼠标充电时显示氛围灯还是充电灯，只有 VT_nrf54L_Base 协议支持。 */
  async setChargeLightDisplayMode(displayMode: BaseChargeLightDisplayMode): Promise<void> {
    const command = this.commands.show_effect
    if (!command) {
      throw new DeviceError(
        DeviceErrorCode.NOT_SUPPORTED,
        'setChargeLightDisplayMode is only supported by VT_nrf54L_Base',
      )
    }

    const commandValue =
      CHARGE_LIGHT_DISPLAY_MODE_COMMAND_VALUE[displayMode as BaseChargeLightDisplayMode]
    if (commandValue == null) {
      throw new DeviceError(
        DeviceErrorCode.INVALID_PARAM,
        `Invalid charge light display mode: ${String(displayMode)}`,
      )
    }

    // 关键协议：show_effect 的 1/0 不是开关，而是氛围灯/充电灯显示策略。
    await sendBaseCommand(this.device, this.profile.config, command, [commandValue])
  }

  /** 读取渐变灯效某个灯珠端点的颜色和亮度。 */
  async getGradientLightEffect(mode: number, ledIndex: number): Promise<BaseGradientLightEffect> {
    const command = this.commands.get_gradient_light_effect
    if (!command) {
      throw new DeviceError(DeviceErrorCode.NOT_SUPPORTED, 'Gradient effect is not supported')
    }

    // P5/P6 的左右渐变按灯珠索引读取：1.0 中常用 0 和 15 分别代表首尾灯。
    const data = await sendBaseCommand(this.device, this.profile.config, command, [mode, ledIndex])
    return {
      mode,
      ledIndex,
      color: [data.getUint8(1), data.getUint8(2), data.getUint8(3)],
      brightness: data.getUint8(4),
      raw: toBytes(data),
    }
  }

  /** 写入渐变灯效某个灯珠端点的颜色和亮度。 */
  async setGradientLightEffect(input: BaseGradientLightEffectInput): Promise<void> {
    const command = this.commands.set_gradient_light_effect
    if (!command) {
      throw new DeviceError(DeviceErrorCode.NOT_SUPPORTED, 'Gradient effect is not supported')
    }

    await sendBaseCommand(this.device, this.profile.config, command, [
      input.mode,
      input.ledIndex,
      ...input.color.slice(0, 3),
      input.brightness,
    ])
  }

  /** 当前芯片底座命令表。 */
  private get commands() {
    return this.profile.commands
  }

  /** 根据灯效目标选择读取命令。 */
  private getLightCommand(target: BaseLightingTarget) {
    return target === LIGHTING_TARGET.BaseLightScreen
      ? this.commands.get_screen_light_effect
      : this.commands.get_light_effect
  }

  /** 根据灯效目标选择写入命令。 */
  private setLightCommand(target: BaseLightingTarget) {
    return target === LIGHTING_TARGET.BaseLightScreen
      ? this.commands.set_screen_light_effect
      : this.commands.set_light_effect
  }

  /** 根据灯效目标选择当前灯效读取命令。 */
  private getCurrentCommand(target: BaseLightingTarget) {
    return target === LIGHTING_TARGET.BaseLightScreen
      ? this.commands.get_current_screen_light_effect
      : this.commands.get_current_light_effect
  }

  /** 根据灯效目标选择当前灯效写入命令。 */
  private setCurrentCommand(target: BaseLightingTarget) {
    return target === LIGHTING_TARGET.BaseLightScreen
      ? this.commands.set_current_screen_light_effect
      : this.commands.set_current_light_effect
  }

  /** 根据 manifest 判断指定模式是否为渐变灯效。 */
  private isGradientEffect(mode: number, target: BaseLightingTarget) {
    const lightingConfig = this.device.manifest.capabilities.find(
      (cap: any) => cap.type === 'lighting',
    ) as LightingCapability | undefined
    const effect = lightingConfig?.config.effects.find(
      (item) => item.target === target && item.id === mode,
    )
    return Boolean(effect?.editableFields.includes(LIGHTING_EDITABLE_FIELD.Gradient))
  }
}
